import { capitalize } from 'lodash';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import { CLIENT_TYPE, Condition } from '@upstart/harmony/lib/constants';
import { TieredAprConfig } from 'components/PricingEditor/Tiers/types';
import {
  AprConfig,
  CarPriceRule,
  Dealer,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { CreditPricing } from '@upstart/harmony/lib/legos/creditPricingType';
import { transformAndConvert } from 'components/PricingEditor/Tiers/creditTiersAdapter';
import { useAprConfig } from '../PricingEditor/Tiers/useAprConfig';
import TiersContainer from '../PricingEditor/Tiers/components/TiersContainer';
import { DealerForPriceMatrix, ModelEntry, ModelYearRangeEntry } from './types';
import { dealerRequest } from '../../redux/dealers/actions';

const useAprConfigModal = ({
  clientType,
  modelEntry,
  modelYearRangeEntry,
  condition,
  aprConfig,
  rule,
  dealer,
  onSave,
  currentTieredAprConfig,
  onSaveForUsedCondition,
  errorMessagesForYearRangeEntry,
}: {
  clientType?: CLIENT_TYPE;
  modelEntry?: ModelEntry;
  modelYearRangeEntry?: ModelYearRangeEntry;
  rule?: Partial<CarPriceRule>;
  condition: Condition;
  aprConfig: AprConfig | undefined;
  dealer?: Dealer | DealerForPriceMatrix;
  trim?: string;
  vin?: string;
  onSave?: (newAprConfig: AprConfig) => void;
  currentTieredAprConfig?: TieredAprConfig | null;
  onSaveForUsedCondition?: (
    creditPricing: CreditPricing,
    tieredAprConfig: TieredAprConfig,
  ) => void;
  errorMessagesForYearRangeEntry?: {
    maxYear: string | null;
    minYear: string | null;
  };
}) => {
  const [isOpen, setIsOpen] = useState(false);

  let label = `${capitalize(condition)} Condition, Default Rule`;
  if (modelYearRangeEntry) {
    label = `Used Condition, Model Year Range: ${modelYearRangeEntry.maxYear ??
      ''} - ${modelYearRangeEntry.minYear ?? ''}`;
  } else if (modelEntry) {
    label = `${modelEntry.year} ${modelEntry.make} ${modelEntry.model}`;
    if (rule?.vin) {
      label += ` ${rule?.vin}`;
    } else if (rule?.mfrModelCodes) {
      label += ` (${rule.mfrModelCodes.map(i => i.code).join(', ')})`;
    }
  }

  const getYearRangeDetails = () => {
    const { maxYear, minYear } = modelYearRangeEntry || {};
    return { maxYear, minYear };
  };

  const getCarDetails = () => {
    const { year, make, model } = modelEntry || {};
    const { trim, vin } = rule || {};
    return {
      year: year?.toString() || '',
      make: make?.toString() || '',
      model: model?.toString() || '',
      trim: trim?.toString() || '',
      vin: vin?.toString() || '',
    };
  };

  const aprConfigResult = useAprConfig({
    dealerId: dealer?.id ?? '',
    section: {
      type: clientType ?? CLIENT_TYPE.ONLINE,
      condition,
      ...(modelYearRangeEntry ? getYearRangeDetails() : getCarDetails()),
    },
    isAprConfigModal: true,
    currentTieredAprConfig,
  });

  const close = () => setIsOpen(false);

  const dispatch = useDispatch();

  const aprConfigModal = isOpen ? (
    <div className="p-modal-container apr-config-modal-container">
      <div className="p-modal apr-config-modal">
        <h4 className="w-full">
          {capitalize(clientType)} APR Configuration - {label}
        </h4>
        <i onClick={close} className="modal-close close icon" />
        <div className="p-modal-body">
          {aprConfigResult && (
            <TiersContainer condition={condition} {...aprConfigResult} />
          )}
        </div>
        <div className="apr-config-buttons">
          <Button
            bsStyle="primary"
            disabled={!aprConfigResult.validationResult.success}
            onClick={async () => {
              if (condition === Condition.USED && onSaveForUsedCondition) {
                aprConfigResult.saveForUsed(onSaveForUsedCondition);
              } else {
                // We should await for the result and then we need to refresh the dealer to get the updated dealer
                // with pricing config. This will help for validating front end errors.
                await aprConfigResult.save();
                const { tieredAprConfig } = aprConfigResult;
                if (onSave && tieredAprConfig) {
                  onSave(transformAndConvert(tieredAprConfig));
                }
                if (dealer?.id) {
                  dispatch(dealerRequest({ dealerId: dealer?.id }));
                }
              }
              close();
            }}
          >
            Save Changes
          </Button>
          <Button bsStyle="default" onClick={close}>
            Cancel
          </Button>
        </div>
        {condition === Condition.USED && onSaveForUsedCondition && (
          <div className="div-notification">
            <span className="span-center">
              {`(Your changes will not be saved immediately after clicking on Save Changes button in this model. You will be still required to click on Save Changes button present outside this model in the previous page. You need to click on both Save Changes buttons for the changes to be saved.)`}{' '}
            </span>
          </div>
        )}
      </div>
    </div>
  ) : null;

  const aprConfigButton = (
    <Button
      type="button"
      bsStyle="default"
      className={
        aprConfig?.enabled ? 'apr-config-edit-button' : 'apr-config-set-button'
      }
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        aprConfigResult.loadPricingRules();
        setIsOpen(true);
      }}
      disabled={
        !!errorMessagesForYearRangeEntry?.maxYear ||
        !!errorMessagesForYearRangeEntry?.minYear
      }
    >
      {aprConfig?.enabled ? 'Edit APR' : 'Set APR'}
    </Button>
  );

  return { aprConfigModal, aprConfigButton, setAprConfigModalOpen: setIsOpen };
};

export default useAprConfigModal;
